// Small pieces the Code tab's parts share: a session's state as a dot, an
// issue's state as a chip, and an age as a dense list writes it. Moved from
// today's pages (code/bits.tsx, components/bits.tsx and lib/time.ts there).
import type { Session, StateSlug } from '../api/types'
import { StateIcon } from '../components/Icons'
import { exactTime } from '../components/Time'
import { Working } from '../components/Working'
import { stateName } from '../vocab'
import { shownState, stateText } from './format'

/**
 * A session's state as arms while working, otherwise a dot; what it says is
 * in its title and for a screen reader. `silent` leaves the words to whoever
 * shows it, where they read better elsewhere than before everything else.
 */
export function StateDot({ session, asking = false, silent = false }: { session: Session; asking?: boolean; silent?: boolean }) {
  const text = stateText(session, asking)
  const shown = shownState(session, asking)
  return (
    <span className={`code-dot is-${shown}`} title={text} aria-hidden={silent || undefined}>
      {shown === 'working' && <Working label={text} aria-hidden="true" />}
      {!silent && <span className="visually-hidden">{text}</span>}
    </span>
  )
}

/** An issue's state as a pill. */
export function StateChip({ state }: { state: StateSlug }) {
  return (
    <span className={`state-chip state-chip-${state}`}>
      <StateIcon state={state} size={12} />
      {stateName(state)}
    </span>
  )
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/** Compact age for dense lists: "now", "5m", "3h", "12d", "4mo", "2y". */
export function shortAge(iso: string, now: number): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const diff = Math.max(0, now - t)
  if (diff < MINUTE) return 'now'
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m`
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h`
  if (diff < 30 * DAY) return `${Math.floor(diff / DAY)}d`
  if (diff < 365 * DAY) return `${Math.floor(diff / (30 * DAY))}mo`
  return `${Math.floor(diff / (365 * DAY))}y`
}

/** A moment as its age in a dense list; the exact time, after what it was, on hover. */
export function Age({ iso, label }: { iso: string; label: string }) {
  if (Number.isNaN(Date.parse(iso))) return null
  return (
    <time dateTime={iso} title={`${label} ${exactTime(iso)}`}>
      {shortAge(iso, Date.now())}
    </time>
  )
}
